import React from 'react';

interface GameShellProps {
  children: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  maxHeightCeiling?: string;
  /**
   * Centraliza verticalmente o bloco interno quando `maxHeightCeiling` é menor
   * que a tela. Sem isso, o bloco com teto gruda no topo (filho único de
   * `justify-between`) e deixa banda morta embaixo — medido: 368px na Vitória
   * em 430×932. Telas full-height não precisam (não muda nada nelas).
   */
  center?: boolean;
  /**
   * Cor "r, g, b" do bioma para a moldura de arena (cantoneiras + glow de borda).
   * Quando definido, o palco ganha o enquadramento de "arena" temático. Passado
   * pelas telas de gameplay (getBiomeTheme(faseAtual).rgb). Decorativo, aria-hidden.
   */
  frameRgb?: string;
  className?: string;
}

/** Cantoneiras de HUD sci-fi nos 4 cantos da coluna, na cor do bioma. */
function ArenaCorners({ rgb }: { rgb: string }) {
  const common = 'absolute w-[clamp(14px,3vmin,26px)] h-[clamp(14px,3vmin,26px)] pointer-events-none';
  const line = { borderColor: `rgba(${rgb}, 0.7)`, filter: `drop-shadow(0 0 5px rgba(${rgb}, 0.6))` };
  return (
    <div aria-hidden="true" className="absolute inset-0 pointer-events-none z-0">
      <span className={`${common} top-0 left-0 border-t-2 border-l-2 rounded-tl-lg`} style={line} />
      <span className={`${common} top-0 right-0 border-t-2 border-r-2 rounded-tr-lg`} style={line} />
      <span className={`${common} bottom-0 left-0 border-b-2 border-l-2 rounded-bl-lg`} style={line} />
      <span className={`${common} bottom-0 right-0 border-b-2 border-r-2 rounded-br-lg`} style={line} />
    </div>
  );
}

/**
 * GameShell — Abstração de layout unificado em 3 camadas Full-Stage Anchor.
 * Garante enquadramento de ponta a ponta (100dvh) sem vazios pretos e sem rolagens indesejadas.
 * - Topo (Header / Status): Ancorado no topo.
 * - Centro (Stage / Content): Expande proporcionalmente (flex-1) preenchendo o palco.
 * - Rodapé (Thumb Zone): Ancorado na base para acionamento ergonômico.
 */
export const GameShell: React.FC<GameShellProps> = ({
  children,
  maxWidth = '2xl',
  maxHeightCeiling = 'max-h-[100dvh]',
  center = false,
  frameRgb,
  className = '',
}) => {
  // Largura da coluna em PX (constante), não em rem. A escala raiz da engine
  // (font-size do html) faz o texto, os controles e o espaçamento crescerem em
  // telas grandes — mas a LARGURA da coluna de jogo deve permanecer com
  // proporção de celular, senão no tablet os botões viram barras esticadas de
  // ~735px. Os valores são iguais aos presets rem @16px (piso 360×640 idêntico);
  // a diferença só aparece em tablet/desktop, onde antes inflavam para ~924px.
  const widthClasses = {
    sm: 'max-w-[384px]',
    md: 'max-w-[448px]',
    lg: 'max-w-[512px]',
    xl: 'max-w-[576px]',
    '2xl': 'max-w-[672px]',
    '3xl': 'max-w-[768px]',
  };

  return (
    <div className="flex-1 h-full max-h-[100dvh] w-full flex flex-col justify-between items-center pad-fluid-x pad-fluid-y mx-auto animate-in fade-in duration-300 relative z-10 overflow-hidden">
      <div
        className={`relative w-full ${widthClasses[maxWidth]} mx-auto ${maxHeightCeiling} h-full flex flex-col justify-between items-center ${center ? 'my-auto' : ''} ${className}`}
        style={frameRgb ? { ['--frame' as string]: frameRgb } : undefined}
      >
        {frameRgb && <ArenaCorners rgb={frameRgb} />}
        {children}
      </div>
    </div>
  );
};
